import { createContext, useContext, type ReactNode } from 'react';
import { useStorefrontData } from './StorefrontDataContext';

export type LiveEditorSaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'unsaved';

export interface LiveEditorScopeValue {
  pageKey: string;
  pageTitle: string;
  data: any;
  updateField: (section: string, field: string, value: any) => void;
  updateSection: (section: string, value: any) => void;
  updateData: (updater: (prev: any) => any) => void;
  toggleSectionVisibility: (sectionId: string) => void;
  isSectionHidden: (sectionId: string) => boolean;
  isEditMode: boolean;
  isPreviewMode: boolean;
  isAdmin: boolean;
  canEdit: boolean;
  setIsEditMode: (mode: boolean) => void;
  setIsPreviewMode: (mode: boolean) => void;
  saveStatus: LiveEditorSaveStatus;
  lastSavedTime: string | null;
  hasUnpublishedChanges: boolean;
  saveDraftManual: () => Promise<boolean>;
  publishToLive: () => Promise<boolean>;
  discardDraftChanges: () => Promise<boolean>;
  sectionsList?: Array<{ id: string; label: string; key?: string }>;
  viewSource?: 'published' | 'draft';
  setViewSource?: (source: 'published' | 'draft') => void;
}

export const LiveEditorScopeContext = createContext<LiveEditorScopeValue | null>(null);

export function LiveEditorScopeProvider({
  value,
  children
}: {
  value: LiveEditorScopeValue;
  children: ReactNode;
}) {
  return (
    <LiveEditorScopeContext.Provider value={value}>
      {children}
    </LiveEditorScopeContext.Provider>
  );
}

/**
 * Universal hook for live editor components (EditableText, EditableMedia, EditableRichText, LivePageDrawer, etc.)
 * Returns the current scoped page live editor context if available, or gracefully falls back to StorefrontDataContext.
 */
export function useLiveEditorScope() {
  const scope = useContext(LiveEditorScopeContext);
  const storefront = useStorefrontData();

  if (scope) {
    return {
      pageKey: scope.pageKey,
      pageTitle: scope.pageTitle,
      data: scope.data,
      updateField: scope.updateField,
      updateSection: scope.updateSection,
      updateData: scope.updateData,
      toggleSectionVisibility: scope.toggleSectionVisibility,
      isSectionHidden: scope.isSectionHidden,
      isEditMode: scope.isEditMode,
      isPreviewMode: scope.isPreviewMode,
      isAdmin: scope.isAdmin,
      canEdit: scope.canEdit,
      setIsEditMode: scope.setIsEditMode,
      setIsPreviewMode: scope.setIsPreviewMode,
      saveStatus: scope.saveStatus,
      lastSavedTime: scope.lastSavedTime,
      hasUnpublishedChanges: scope.hasUnpublishedChanges,
      saveDraftManual: scope.saveDraftManual,
      publishToLive: scope.publishToLive,
      discardDraftChanges: scope.discardDraftChanges,
      sectionsList: scope.sectionsList,
      viewSource: scope.viewSource,
      setViewSource: scope.setViewSource,
      // Alias for compatibility with components expecting cms or updateCmsField
      cms: scope.data,
      updateCmsField: scope.updateField,
      cdnBaseUrl: storefront?.cdnBaseUrl || '',
    };
  }

  const isHidden = (secId: string) => {
    const hiddenList = storefront?.cms?.hiddenSections || [];
    return Array.isArray(hiddenList) && hiddenList.includes(secId);
  };

  const toggleHidden = (secId: string) => {
    const current = storefront?.cms?.hiddenSections || [];
    const next = current.includes(secId)
      ? current.filter((id: string) => id !== secId)
      : [...current, secId];
    storefront?.updateWholeCms?.((prev: any) => ({
      ...prev,
      hiddenSections: next,
    }));
  };

  return {
    pageKey: 'main_site',
    pageTitle: 'Homepage',
    data: storefront?.cms,
    cms: storefront?.cms,
    updateField: storefront?.updateCmsField,
    updateCmsField: storefront?.updateCmsField,
    updateSection: storefront?.updateCmsSection,
    updateData: storefront?.updateWholeCms,
    toggleSectionVisibility: toggleHidden,
    isSectionHidden: isHidden,
    isEditMode: storefront?.isEditMode ?? false,
    isPreviewMode: storefront?.isPreviewMode ?? false,
    isAdmin: storefront?.isAdmin ?? false,
    canEdit: Boolean(storefront?.isAdmin && storefront?.isEditMode && !storefront?.isPreviewMode),
    setIsEditMode: storefront?.setIsEditMode,
    setIsPreviewMode: storefront?.setIsPreviewMode,
    saveStatus: (storefront?.saveStatus as LiveEditorSaveStatus) || 'idle',
    lastSavedTime: storefront?.lastSavedTime ?? null,
    hasUnpublishedChanges: storefront?.hasUnpublishedChanges ?? false,
    saveDraftManual: storefront?.saveDraftManual,
    publishToLive: storefront?.publishToLive,
    discardDraftChanges: storefront?.discardDraftChanges,
    sectionsList: undefined,
    viewSource: (storefront as any)?.viewSource || 'published',
    setViewSource: (storefront as any)?.setViewSource,
    cdnBaseUrl: storefront?.cdnBaseUrl || '',
  };
}
